// Le versement d'un tampon Honeywell, côté PC.
//
// C'est le dernier maillon, et le seul qui écrive. L'appareil capture dans son
// tampon et s'arrête là (migration 11) : sans cet écran, tout ce qui est scanné
// au coffre ou en surface reste dans `fc_hw_sessions` et n'atteint jamais la
// journée. Un tampon livré que personne ne verse, c'est le travail fait deux
// fois — une fois au terminal, une fois au clavier.
//
// ON MONTRE AVANT D'ÉCRIRE, et ce n'est pas une politesse d'interface. Le
// tampon arrive d'un appareil qu'on n'avait pas sous les yeux, rempli par
// quelqu'un d'autre peut-être, il y a deux heures peut-être. Un bouton
// « verser » sans revue serait un bouton qu'on n'ose pas presser — donc un
// bouton qu'on ne presse pas, donc la saisie refaite à la main.
//
// CHAQUE LIGNE SE DÉCOCHE. Réviser sans pouvoir refuser ne serait que
// regarder : on verrait la ligne fausse passer et on la verserait quand même.
//
// Le calcul, lui, n'est pas ici. `fc_hw_apercu` dit ce que le versement ferait,
// `fc_hw_verser` le fait ; cet écran n'a aucune opinion sur les tickets ni sur
// les enveloppes. Refaire le recoupement en TypeScript en aurait fait une
// seconde vérité — c'est exactement cette dérive qui avait rendu deux verdicts
// silencieusement morts côté serveur.

import { useEffect, useMemo, useState } from 'react'
import * as api from '@/data/api'
import {
  COULEUR_VERDICT, LIBELLE_MODULE, LIBELLE_VERDICT, compteAVerser, modulesDeLEtape,
  phraseVersement, type Apercu, type LigneApercu, type SessionEnAttente,
} from '@/domain/hw'
import { fmtEur, heure, pluriel } from '@/lib/format'
import { messageErreur } from '@/lib/errors'
import { Chip, Modale, Notice, Vide } from '@/ui/primitives'
import { useToast } from '@/ui/Toast'

const euros = (cents: number | null | undefined): string =>
  cents == null ? '—' : fmtEur(cents / 100)

const nombre = (v: unknown): number => (typeof v === 'number' ? v : Number(v) || 0)

/**
 * Le panneau posé en tête d'étape. Trois états, et un seul dit quelque chose :
 * pas de tampon (l'appareil est disponible), un tampon livré (il attend), la
 * capture en cours (on ne montre RIEN de son contenu — §5.2.3, l'étape ne bouge
 * pas pendant qu'on est au coffre).
 */
export function PanneauVersement({ date, code, lectureSeule, attente, apresVersement }: {
  date: string
  code: string
  lectureSeule: boolean
  /** Tous les tampons livrés de la run — le panneau ne garde que les siens. */
  attente: SessionEnAttente[]
  apresVersement: () => Promise<unknown> | unknown
}) {
  const modules = useMemo(() => modulesDeLEtape(code), [code])
  const miens = useMemo(() => attente.filter((s) => modules.includes(s.module)), [attente, modules])
  const [ouverte, setOuverte] = useState<string | null>(null)
  const toast = useToast()

  if (modules.length === 0) return null

  if (miens.length === 0) {
    return (
      <Notice c="hw">
        <b>Capture Honeywell disponible</b> — module{modules.length > 1 ? 's' : ''}{' '}
        {modules.map((m) => LIBELLE_MODULE[m].toLowerCase()).join(' + ')}.
        <div className="hint" style={{ marginTop: 2 }}>
          LE TERMINAL CAPTURE DANS SON TAMPON · LE TAMPON REVIENT ICI POUR ÊTRE RÉVISÉ PUIS VERSÉ
        </div>
      </Notice>
    )
  }

  return (
    <>
      {miens.map((s) => (
        <Notice key={s.id} c="hw" action={
          <button className="btn hw" onClick={() => setOuverte(s.id)}>
            Réviser et verser
          </button>
        }>
          <b>Un tampon {LIBELLE_MODULE[s.module].toLowerCase()} attend</b> —{' '}
          {pluriel(s.n, 'élément')} capturé{s.n > 1 ? 's' : ''}
          {s.livree_par ? ` par ${s.livree_par}` : ''}
          {s.livree_at ? `, livré à ${heure(s.livree_at)}` : ''}.
          <div className="hint" style={{ marginTop: 2 }}>
            RIEN N’EST ENCORE ENTRÉ DANS L’ÉTAPE · LA RÉVISION N’ÉCRIT RIEN
          </div>
        </Notice>
      ))}

      {ouverte && (
        <ModaleVersement
          date={date} session={ouverte} lectureSeule={lectureSeule}
          fermer={() => setOuverte(null)}
          verse={async (n) => {
            setOuverte(null)
            await apresVersement()
            toast(n > 0 ? `TAMPON VERSÉ — ${n} ÉCRITURE${n > 1 ? 'S' : ''}` : 'TAMPON CLOS — RIEN À ÉCRIRE')
          }}
        />
      )}
    </>
  )
}

// ── La revue ───────────────────────────────────────────────────────────────

function ModaleVersement({ date, session, lectureSeule, fermer, verse }: {
  date: string
  session: string
  lectureSeule: boolean
  fermer: () => void
  verse: (ecrits: number) => Promise<void>
}) {
  const [ap, setAp] = useState<Apercu | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [ignores, setIgnores] = useState<Set<string>>(new Set())
  const [occupe, setOccupe] = useState(false)
  const toast = useToast()

  useEffect(() => {
    let vivant = true
    api.hwApercu(date, session)
      .then((a) => { if (vivant) setAp(a) })
      .catch((e) => { if (vivant) setErreur(messageErreur(e)) })
    return () => { vivant = false }
  }, [date, session])

  const basculer = (id: string) => setIgnores((s) => {
    const n = new Set(s)
    if (n.has(id)) n.delete(id); else n.add(id)
    return n
  })

  const aVerser = ap ? compteAVerser(ap.lignes, ignores) : 0
  const fige = lectureSeule || !!ap?.bloquant || occupe

  const verser = async () => {
    if (!ap || occupe) return
    setOccupe(true)
    try {
      const r = await api.hwVerser(date, session, [...ignores])
      await verse(r.ecrits)
    } catch (e) {
      toast(messageErreur(e), true)
      setOccupe(false)
    }
  }

  return (
    <Modale
      large
      titre={ap ? `Tampon ${LIBELLE_MODULE[ap.module].toLowerCase()}` : 'Tampon Honeywell'}
      sous={ap
        ? `${pluriel(ap.n, 'élément')} capturé${ap.n > 1 ? 's' : ''}`
          + `${ap.livree_par ? ` par ${ap.livree_par}` : ''}`
          + `${ap.livree_at ? ` · livré à ${heure(ap.livree_at)}` : ''}`
        : undefined}
      fermer={fermer}
    >
      {erreur && <Notice c="stop"><b>Lecture impossible</b> — {erreur}</Notice>}
      {!ap && !erreur && <Vide>Lecture du tampon…</Vide>}

      {ap && (
        <>
          {ap.bloquant && (
            <Notice c="warn">
              <b>Versement impossible</b> — {ap.bloquant}.
            </Notice>
          )}

          {/* Le compte, avant le détail : c'est lui qu'on lit d'abord. */}
          <div className="kpis" style={{ marginBottom: 12 }}>
            <Compteur k="à verser" v={aVerser} s={ignores.size > 0 ? `${ignores.size} décochée${ignores.size > 1 ? 's' : ''}` : 'écritures'} />
            <Compteur k="déjà fait" v={ap.resume?.deja ?? 0} s="laissé tel quel" />
            <Compteur k="introuvable" v={ap.resume?.absent ?? 0} s="hors import du jour" />
            <Compteur k="contrôle" v={ap.resume?.info ?? 0} s="n’écrit rien" />
          </div>

          <Controle ap={ap} />

          <div style={{ maxHeight: 340, overflowY: 'auto', border: '1px solid var(--line2)' }}>
            {ap.lignes.length === 0
              ? <Vide>Ce tampon ne porte rien qui puisse être versé.</Vide>
              : (
                <table className="t">
                  <thead>
                    <tr>
                      <th style={{ width: 30 }} />
                      <th>Élément</th>
                      <th className="n">Montant</th>
                      <th style={{ width: 108 }}>Sort</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ap.lignes.map((l) => (
                      <Ligne key={l.id} l={l} ignore={ignores.has(l.id)}
                             fige={fige} basculer={() => basculer(l.id)} />
                    ))}
                  </tbody>
                </table>
              )}
          </div>

          <p className="hint" style={{ marginTop: 10 }}>
            RIEN N’EST ÉCRIT TANT QUE TU N’AS PAS VERSÉ · UNE DÉCISION DÉJÀ PRISE SUR LE PC N’EST JAMAIS RECOUVERTE
          </p>

          <div className="btns">
            <button className="btn gh" onClick={fermer} disabled={occupe}>Fermer sans verser</button>
            <button className="btn pri" onClick={() => void verser()}
                    disabled={fige || aVerser === 0}
                    data-encours={occupe ? 'true' : undefined}>
              {phraseVersement(aVerser, ap.module)}
            </button>
          </div>
        </>
      )}
    </Modale>
  )
}

const Compteur = ({ k, v, s }: { k: string; v: number; s: string }) => (
  <div className="kpi">
    <div className="k">{k}</div>
    <div className="v">{v}</div>
    <div className="s">{s}</div>
  </div>
)

function Ligne({ l, ignore, fige, basculer }: {
  l: LigneApercu; ignore: boolean; fige: boolean; basculer: () => void
}) {
  const decochable = l.verdict === 'a_verser' && !fige
  return (
    <tr className={l.verdict === 'a_verser' && !ignore ? 'hwrow' : undefined}>
      <td>
        {/* Seule une ligne qui VA écrire se décoche : refuser une ligne qui
            n'écrit rien ne voudrait rien dire. */}
        {l.verdict === 'a_verser' && (
          <input type="checkbox" checked={!ignore} disabled={!decochable}
                 onChange={basculer} aria-label={`verser ${l.titre}`} />
        )}
      </td>
      <td>
        <div style={{ fontWeight: 600, textDecoration: ignore ? 'line-through' : undefined }}>
          {l.titre}
        </div>
        <div className="hint">{l.sous}</div>
        {l.motif ? <div className="hint">motif : {l.motif}</div> : null}
      </td>
      <td className="n">
        {l.qte != null ? l.qte
          : l.montant_cents == null && l.geste === 'enveloppe'
            ? <span className="hint">déjà compté</span>
            : euros(l.montant_cents)}
      </td>
      <td>
        <Chip c={ignore ? 'warn' : COULEUR_VERDICT[l.verdict]}>
          {ignore ? 'DÉCOCHÉE' : LIBELLE_VERDICT[l.verdict]}
        </Chip>
      </td>
    </tr>
  )
}

// ── Le contrôle, par module ────────────────────────────────────────────────
// C'est la moitié utile du versement. Le coffre n'écrit que des quantités, le
// dépouillement n'écrit que des cases cochées — mais tous deux RAPPROCHENT le
// physique de ce que la journée annonce, et c'est ce rapprochement qu'on vient
// lire. On le montre donc avant la liste, pas après.

function Controle({ ap }: { ap: Apercu }) {
  const c = ap.cible ?? {}

  if (ap.module === 'coffre') {
    const declarees = nombre(c.sco_declarees)
    const trouvees = nombre(c.trouvees)
    const ajoutees = nombre(c.ajoutees)
    const ecart = nombre(c.ecart_enveloppes)
    return (
      <div style={{ marginBottom: 12 }}>
        <Notice c={ecart === 0 ? 'ok' : 'warn'}>
          <b>
            {trouvees} enveloppe{trouvees > 1 ? 's' : ''} trouvée{trouvees > 1 ? 's' : ''} au coffre
            {' '}pour {declarees} déclarée{declarees > 1 ? 's' : ''} au vidage SCO
          </b>
          {' — '}
          {ecart === 0
            ? 'le compte y est'
            : ecart > 0
              ? `${ecart} de plus qu’annoncé`
              : `${-ecart} enveloppe${-ecart > 1 ? 's' : ''} n’a pas été retrouvée`}.
          <div className="hint" style={{ marginTop: 2 }}>
            LEUR ARGENT EST DÉJÀ COMPTÉ PAR L’ÉTAPE 05B · LE SCAN N’INSCRIT QUE LE NUMÉRO DE SCELLÉ
            {ajoutees > 0 && ` · ${ajoutees} ENVELOPPE${ajoutees > 1 ? 'S' : ''} EN PLUS COMPTERA${ajoutees > 1 ? 'ONT' : ''} 500 €`}
          </div>
        </Notice>
        <div className="hint" style={{ marginTop: 6 }}>
          {pluriel(nombre(c.cases), 'case')} comptée{nombre(c.cases) > 1 ? 's' : ''} au terminal —
          elles remplacent la quantité de l’étape, case par case, sans toucher aux autres.
        </div>
      </div>
    )
  }

  if (ap.module === 'formation') {
    const sans = nombre(c.sans_motif)
    if (sans === 0) return null
    return (
      <Notice c="warn">
        <b>{pluriel(sans, 'ticket')} sans motif</b> — la formation en réclame un pour chaque
        ticket (O9). Ils se verseront, mais l’étape restera bloquée tant que le motif manque.
      </Notice>
    )
  }

  if (ap.module === 'depouillement') {
    const classes = (c.classes ?? []) as Array<Record<string, unknown>>
    const bonsO = (c.bons_orika ?? {}) as Record<string, unknown>
    const chqO = (c.cheques_orika ?? {}) as Record<string, unknown>
    const rattaches = nombre(c.rattaches)
    const enEcart = classes.filter((x) => nombre(x.ecart) !== 0)
    if (classes.length === 0 && rattaches === 0) return null

    return (
      <div style={{ marginBottom: 12 }}>
        {classes.length > 0 && (
          <>
            {/* Un bon divers n'a AUCUNE référence dans ORIKA : on ne peut pas
                dire quelle ligne il est, seulement combien il y en a. La
                colonne « écart » est donc la seule qui se lise vraiment. */}
            <table className="t" style={{ border: '1px solid var(--line2)' }}>
              <thead>
                <tr>
                  <th>Classe<div className="hint">caissier · nature · montant</div></th>
                  <th className="n">En main</th><th className="n">Chez ORIKA</th><th className="n">Écart</th>
                </tr>
              </thead>
              <tbody>
                {classes.map((x, i) => {
                  const ecart = nombre(x.ecart)
                  return (
                    <tr key={i}>
                      <td>
                        {x.caissier ? `Caissier ${String(x.caissier)}` : 'En vrac'}
                        {' · '}{String(x.nature) === 'cheque' ? 'chèque' : 'bon'}
                        {' · '}{euros(nombre(x.cents))}
                        {!x.caissier && nombre(x.chez_orika) > 1 && (
                          <div className="hint">
                            plusieurs règlements à ce montant — seule une pochette permet de rattacher
                          </div>
                        )}
                      </td>
                      <td className="n">{nombre(x.en_main)}</td>
                      <td className="n">{nombre(x.chez_orika)}</td>
                      {/* D14 : un écart n'est jamais vert, et sa couleur ne dépend pas de son signe. */}
                      <td className="n" style={{ color: ecart === 0 ? undefined : 'var(--warn-b)' }}>
                        {ecart === 0 ? '—' : ecart > 0 ? `+${ecart}` : ecart}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <div className="hint" style={{ marginTop: 6 }}>
              {rattaches > 0
                ? `${pluriel(rattaches, 'règlement')} sera${rattaches > 1 ? 'ont' : ''} coché${rattaches > 1 ? 's' : ''} · `
                : ''}
              ORIKA annonce {nombre(bonsO.n)} bons ({euros(nombre(bonsO.cents))})
              et {nombre(chqO.n)} chèques ({euros(nombre(chqO.cents))}) sur la journée entière.
            </div>
          </>
        )}
        {enEcart.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <Notice c="warn">
              <b>{pluriel(enEcart.length, 'classe')} en écart</b> — le compte des papiers ne tombe
              pas sur celui des lignes de caisse. Les lignes concernées ne sont pas cochées.
            </Notice>
          </div>
        )}
      </div>
    )
  }

  return null
}
